import {runInAction} from "mobx";
import type {
	account, accountUuid,
	project, projectUuid,
	topic, topicUuid,
	annotation, annotationUuid, annotationCitation,
	accessTokenUuid
} from "@digintent/pn-highlight-lib/model";
import {Data, Utils, Citation} from "@digintent/pn-highlight-lib";
import ApiUtils from "../../utils/api.js";
import {Citation as HCitation} from "@digintent/pn-highlight-lib/integrations";

const now = new Date();
const pageUrl = window.location.href;
const dummyCitationData = Citation.fromCSL({
	"type":        "webpage",
	"title":       "Getting started",
	"title-short": "PowerNotes",
	"accessed":    {"date-parts": [[
		"" + now.getFullYear(),
		"" + (1 + now.getMonth()),
		"" + now.getDate()
	]]},
	"container-title": "PowerNotes",
	"URL":             pageUrl
} as any);
const dummyCitationRendered = "<p>“Getting Started”. <i>PowerNotes</i>, "
	+ pageUrl + ". Accessed "
	+ now.toLocaleDateString("en", {day: "numeric"}) + " "
	+ now.toLocaleDateString("en", {month: "short"}) + " "
	+ now.toLocaleDateString("en", {year: "numeric"}) + ".</p>";
const dummyCitation = {
	"version":           2,
	"approved":          true,
	"structured":        true,
	"data":              dummyCitationData,
	"render_format":     "modern-language-association",
	"rendered_citation": dummyCitationRendered,
	"data_modified":     true,
	"render_modified":   true,
	"source_notes":      [{"label": "Source Note", "note": "", "position": 0}],
	"approve_dt":        "2024-05-10T16:19:50.960Z"
} as annotationCitation;
HCitation._getAutoGeneratedCitationData = async () => dummyCitation;

Data.app.init({
	backend: null,
	sync:    null,
	storage: null,
	urls:    {
		promo:           LEO.urls.promo,
		pn_www:          LEO.urls.www,
		pn_api:          LEO.urls.api + "v1/",
		pn_comments_api: LEO.urls.comments_api + "v1/",
		pn_files_api:    LEO.urls.files_api + "v1/",
		pn_auth_ws:      LEO.urls.ws
	},
	userAgent: window.navigator.userAgent,
	client:    "demo",
	version:   "0.0.0"
}, {
	// outSyncHandler,
	// backendErrorHandler,
	// authFailHandler
});

runInAction(() => {
	// Use the real API for this (unauthenticated endpoint)
	Data.app.backend.getRenderedCitation = async citation => {
		const url = ApiUtils.getUrl("v1/citations");
		const response = await ApiUtils.post(url, citation) as
			unknown as {data: {rendered_citation: string}};
		return response.data.rendered_citation;
	};

	const accountUuid = Utils.Uuid.base64() as accountUuid;
	const projectUuid = Utils.Uuid.base64() as projectUuid;
	const topicUuid = Utils.Uuid.base64() as topicUuid;
	const topicUuid2 = Utils.Uuid.base64() as topicUuid;
	const annotationUuid = Utils.Uuid.base64() as annotationUuid;
	const annotationUuid2 = Utils.Uuid.base64() as annotationUuid;
	Data.account._accounts = {
		[accountUuid]: {
			uuid:         accountUuid,
			name:         "Demo account",
			subscription: {
				type:      "free",
				status:    "active",
				expire_dt: null
			},
			features: [
				{feature_name: "nprojects", expire_dt: null, n_projects: 1},
				{feature_name: "write", expire_dt: null},
				{feature_name: "trial", expire_dt: null},
				{feature_name: "basic", expire_dt: null},
				{feature_name: "read", expire_dt: null}
			],
			available_projects: 1,
			computed:           {}
		} as unknown as account
	};
	Data.annotation._annotations = {
		[projectUuid]: {
			[annotationUuid]: {
				"uuid":         annotationUuid,
				"account_uuid": accountUuid,
				"version":      "2",
				"type":         "highlight",
				"pageData":     {
					"pageUrl":   pageUrl,
					"pageTitle": "PowerNotes"
				},
				"selection": {
					"startTreeStr": "div(1)/div(1)/div(1)/div(1)/h1(1)",
					"startOffset":  29,
					"endTreeStr":   "div(1)/div(1)/div(1)/div(1)/h1(1)",
					"endOffset":    48
				},
				"selectedText": "highlight something",
				"citation":     dummyCitation,
				"comment":      "<p>This is an example annotation you can add to your highlights.</p>"
			} as unknown as annotation,
			[annotationUuid2]: {
				"uuid":         "9PBYQllLQR2zVzGfdT3dQw",
				"account_uuid": "ffcvn_pVRp6Skh5KzRbi8Q",
				"version":      "2",
				"type":         "freeform",
				"comment":      "<p>Go to <a target=\"_blank\" rel=\"noopener noreferrer nofollow\" href=\"https://app.powernotes.com/account/signup\">app.powernotes.com/account/signup</a> to sign up for your PowerNotes account!</p>"
			} as unknown as annotation
		}
	};
	Data.project._projects = {
		[projectUuid]: {
			uuid:         projectUuid,
			account_uuid: accountUuid,
			name:         "Demo project",
			status:       "active",
			computed:     {}
		} as unknown as project
	};
	Data.outline._outlines = {
		[projectUuid]: [
			{
				"topic_uuid":  topicUuid,
				"annotations": [
					{"annotation_uuid": annotationUuid, "position": 1}
				]
			},
			{
				"topic_uuid":  topicUuid2,
				"annotations": [
					{"annotation_uuid": annotationUuid2, "position": 2}
				]
			}
		]
	};
	Data.topic._topics = {
		[topicUuid]: {
			uuid:         topicUuid,
			account_uuid: accountUuid,
			project_uuid: projectUuid,
			name:         "background",
			label:        "Background",
			color:        "486ea6",
			position:     1
		} as unknown as topic,
		[topicUuid2]: {
			uuid:         topicUuid2,
			account_uuid: accountUuid,
			project_uuid: projectUuid,
			name:         "new-topic",
			label:        "New Topic",
			color:        "e01c10",
			position:     2
		} as unknown as topic
	};
	Data.topic._projectsLoaded = {
		[projectUuid]: true
	};

	const dummyAccessToken = Utils.Uuid.base64() as accessTokenUuid;
	Data.app.login(accountUuid, dummyAccessToken);
	Data.app.setCurrentProject(projectUuid);
});

// Make it accessible to the console for debugging (local env only)
// eslint-disable-next-line no-undef
PowerNotes.Data = Data;

export default Data;
